import { ref, computed, watch, onMounted, unref } from 'vue';
import type { Ref } from 'vue';
import { get } from 'lodash';

import { useNotify } from '@/composables';
import assetVisitService from '@satellite/services/asset-visit-service';

type Asset = {
  id?: string;
  code?: string;
  type?: string;
};

export interface UseYardAssetPresenterOptions {
  assetId?: Ref<string | null | undefined>;
  asset?: Ref<Asset | null | undefined>;
}

export function useYardAssetPresenter(
  { assetId, asset }: UseYardAssetPresenterOptions = {},
  lazy = false
) {
  const notify = useNotify();

  const spotAssignmentData = ref(null);
  const isLoading = ref(false);

  const currentAsset = computed(() => {
    return {
      ...asset?.value,
      ...spotAssignmentData.value
    };
  });

  const assetCode = computed(() => {
    if (!currentAsset.value) {
      return null;
    }

    return currentAsset.value.code || null;
  });

  const assetType = computed(() => {
    if (!currentAsset.value) {
      return null;
    }

    return currentAsset.value.type || null;
  });

  const visitType = computed(() => {
    if (!currentAsset.value) {
      return null;
    }

    return currentAsset.value.assetVisit?.visitType || null;
  });

  const spotId = computed(() => {
    if (!currentAsset.value) {
      return null;
    }

    return (
      get(currentAsset.value, 'spotAssignments[0].spotId') ??
      get(currentAsset.value, 'assetVisit.spotAssignments[0].spotId') ??
      null
    );
  });

  const fetchSpotAssignmentData = async id => {
    if (!id) {
      return;
    }

    isLoading.value = true;

    try {
      const response = await assetVisitService.getAssetContainerById(id);
      spotAssignmentData.value = response;
    } catch (error) {
      notify('Error fetching asset details', 'error');
      console.error('Error fetching asset details:', error);
    } finally {
      isLoading.value = false;
    }
  };

  const refresh = () => {
    spotAssignmentData.value = null;

    const currentAssetIdValue = unref(assetId);
    const currentAssetValue = unref(asset);

    const id = currentAssetIdValue || currentAssetValue?.id;

    if (id) {
      fetchSpotAssignmentData(id);
    }
  };

  watch([() => unref(assetId), () => unref(asset)?.id], () => refresh());

  onMounted(() => {
    if (!lazy) {
      refresh();
    }
  });

  return {
    isLoading,
    assetCode,
    assetType,
    spotId,
    visitType,

    refresh
  };
}
